import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { TechnologyDto, TechnologyWithAuditDto } from "@/types";
import { PublicCategory } from "@/types/common";
import { CreateTechnologyInput, UpdateTechnologyInput } from "@/schemas";
import { RelatedObjectType } from "@/enums";

export type CreateTechnologyRequest = CreateTechnologyInput;
export type UpdateTechnologyRequest = UpdateTechnologyInput;

/**
 * Fetches all technologies across all accounts for the current organization.
 *
 * @returns A list of all technologies in the organization.
 * @throws An error if the request fails.
 */
export async function fetchAllTechnologies(): Promise<TechnologyDto[]> {
  try {
    return await apiClient<TechnologyDto[]>("/api/technologies/all", {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchAllTechnologies");
    throw error;
  }
}

/**
 * Creates a new technology by sending the provided data to the backend.
 *
 * @param data - The data required to create a new technology.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createTechnology(
  data: CreateTechnologyRequest
): Promise<TechnologyDto> {
  try {
    const accountId = data.accountId;
    return await apiClient<TechnologyDto>(
      `/api/accounts/${accountId}/technologies`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "createTechnology");
    throw error;
  }
}

/**
 * Fetches all technologies for a specific account.
 *
 * @param accountId - The ID of the account to fetch technologies for
 * @returns A list of technologies.
 * @throws An error if the request fails.
 */
export async function fetchTechnologiesByAccountId(
  accountId: string
): Promise<TechnologyDto[]> {
  try {
    return await apiClient<TechnologyDto[]>(
      `/api/accounts/${accountId}/technologies`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchTechnologiesByAccountId");
    throw error;
  }
}

/**
 * Fetches a single technology by ID.
 *
 * @param technologyId - The ID of the technology to fetch
 * @returns The technology data with audit information.
 * @throws An error if the request fails.
 */
export async function fetchTechnologyById(
  technologyId: string
): Promise<TechnologyWithAuditDto> {
  try {
    return await apiClient<TechnologyWithAuditDto>(
      `/api/technologies/${technologyId}`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchTechnologyById");
    throw error;
  }
}

/**
 * Updates an existing technology by sending the provided data to the backend.
 *
 * @param technologyId - The ID of the technology to update
 * @param data - The data to update the technology with
 * @returns A promise that resolves with the updated technology data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateTechnology(
  technologyId: string,
  data: UpdateTechnologyRequest
): Promise<TechnologyWithAuditDto> {
  try {
    return await apiClient<TechnologyWithAuditDto>(
      `/api/technologies/${technologyId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "updateTechnology");
    throw error;
  }
}

/**
 * Fetches related objects for a technology by type(s).
 *
 * @param technologyId - The ID of the technology
 * @param types - Array of related object types (e.g., ["ACTIVITY", "GOAL"])
 * @returns An object with arrays of related objects grouped by type.
 * @throws An error if the request fails.
 */
export async function fetchTechnologyRelatedObjects(
  technologyId: string,
  types: RelatedObjectType[]
): Promise<Record<string, unknown[]>> {
  try {
    if (!technologyId || !types.length) return {};
    const params = new URLSearchParams();
    types.forEach((t) => params.append("type", t));
    return await apiClient<Record<string, unknown[]>>(
      `/api/technologies/${technologyId}/related-objects?${params.toString()}`
    );
  } catch (error) {
    logError(error, "fetchTechnologyRelatedObjects");
    throw error;
  }
}

/**
 * Deletes a technology by its ID.
 * @param technologyId - The ID of the technology to delete
 * @returns A promise that resolves when the technology is deleted.
 * @throws An error if the request fails or if the technology is not found.
 */
export async function deleteTechnology(technologyId: string): Promise<void> {
  try {
    await apiClient<void>(`/api/technologies/${technologyId}`, {
      method: "DELETE",
    });
  } catch (error) {
    logError(error, "deleteTechnology");
    throw error;
  }
}

/**
 * Fetches all technology categories.
 *
 * @returns A list of technology categories.
 * @throws An error if the request fails.
 */
export async function fetchTechnologyCategories(): Promise<PublicCategory[]> {
  try {
    return await apiClient<PublicCategory[]>("/api/technology/categories", {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchTechnologyCategories");
    throw error;
  }
}
